import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ChevronDown, LogOut, X, Phone, Mail, Award } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { cn } from '@/lib/utils';
import {
  getNavItems,
  isPathActive,
  isNavGroup,
  isGroupActive,
  DRAWER_WIDTH,
  NavItem,
  NavGroup,
  NavConfig,
  ADMIN_HOME,
} from '@/lib/navigation';

interface SideNavProps {
  isOpen: boolean;
  onClose: () => void;
}

export function SideNav({ isOpen, onClose }: SideNavProps) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  const role = user?.role;
  const navItems = role ? getNavItems(role, user?.partnerType) : [];

  // Auto-expand groups with active children
  useEffect(() => {
    const newExpanded = new Set<string>();
    navItems.forEach((item) => {
      if (isNavGroup(item) && isGroupActive(item, location.pathname)) {
        newExpanded.add(item.id);
      }
    });
    setExpandedGroups(newExpanded);
  }, [location.pathname, navItems]);

  // Close drawer on route change (mobile)
  useEffect(() => {
    onClose();
  }, [location.pathname, onClose]);

  const toggleGroup = (groupId: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }
      return next;
    });
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const renderNavItem = (item: NavItem, depth: number = 0) => {
    const Icon = item.icon;
    const isActive = isPathActive(item.path, location.pathname);

    return (
      <Link
        key={item.id}
        to={item.path}
        className={cn(
          'flex items-center gap-3 px-4 py-2.5 text-sm rounded-lg mx-2 transition-colors',
          depth > 0 && 'ml-6',
          isActive
            ? 'bg-abby-primary/10 text-abby-primary font-medium'
            : 'text-gray-700 hover:bg-gray-100'
        )}
      >
        <Icon className="h-5 w-5 flex-shrink-0" />
        <span>{item.label}</span>
      </Link>
    );
  };

  const renderNavGroup = (group: NavGroup) => {
    const isExpanded = expandedGroups.has(group.id);
    const hasActiveChild = isGroupActive(group, location.pathname);
    const Icon = group.icon;

    return (
      <div key={group.id}>
        <button
          onClick={() => toggleGroup(group.id)}
          className={cn(
            'flex items-center justify-between w-full px-4 py-2.5 text-sm rounded-lg mx-2 transition-colors',
            hasActiveChild
              ? 'text-abby-primary font-medium'
              : 'text-gray-700 hover:bg-gray-100'
          )}
          style={{ width: 'calc(100% - 1rem)' }}
        >
          <div className="flex items-center gap-3">
            {Icon && <Icon className="h-5 w-5 flex-shrink-0" />}
            <span>{group.label}</span>
          </div>
          <ChevronDown
            className={cn(
              'h-4 w-4 transition-transform',
              isExpanded && 'rotate-180'
            )}
          />
        </button>
        {isExpanded && (
          <div className="mt-1 space-y-1">
            {group.children.map((child) => renderNavItem(child, 1))}
          </div>
        )}
      </div>
    );
  };

  const renderNavItems = (items: NavConfig) => {
    return items.map((item) => {
      if (isNavGroup(item)) {
        return renderNavGroup(item);
      }
      return renderNavItem(item);
    });
  };

  const sidebarContent = (
    <div className="flex flex-col h-full">
      {/* Logo section */}
      <div className="flex items-center justify-between h-16 px-4 border-b flex-shrink-0">
        <Link to={role === 'admin' ? ADMIN_HOME : '/'} className="flex items-center gap-3">
          <img src="/Abby_Logo.png" alt="Abby Connect" className="h-8" />
          <span className="text-sm font-semibold text-gray-900">
            Partner Portal
          </span>
        </Link>
        {/* Close button - only visible on mobile */}
        <button
          onClick={onClose}
          className="md:hidden p-1 rounded-lg hover:bg-gray-100 text-gray-500"
          aria-label="Close menu"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* Navigation items */}
      <nav className="flex-1 py-4 space-y-1 overflow-y-auto">
        {renderNavItems(navItems)}
      </nav>

      {/* Partner info block - tier and support contact, for partner users */}
      {user && user.role === 'partner' && (
        <div className="border-t p-4 flex-shrink-0">
          {/* Tier */}
          <div className="flex items-center gap-2 mb-3">
            <Award className="h-4 w-4 text-abby-primary flex-shrink-0" />
            <span className="text-sm font-medium text-gray-900">
              {user.resellerTier || 'Tier 1'}
            </span>
          </div>

          {/* Support Contact */}
          <div className="space-y-2">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">
              Support
            </p>
            <p className="text-sm text-gray-900">
              {user.resellerSupportName || 'Reseller Support'}
            </p>
            <a
              href={`tel:${user.resellerSupportPhone || '702-835-6800'}`}
              className="flex items-center gap-2 text-sm text-abby-primary hover:underline"
            >
              <Phone className="h-3.5 w-3.5 flex-shrink-0" />
              {user.resellerSupportPhone || '702-835-6800'}
            </a>
            {user.resellerSupportEmail && (
              <a
                href={`mailto:${user.resellerSupportEmail}`}
                className="flex items-center gap-2 text-sm text-abby-primary hover:underline"
              >
                <Mail className="h-3.5 w-3.5 flex-shrink-0" />
                {user.resellerSupportEmail}
              </a>
            )}
          </div>
        </div>
      )}

      {/* User block at bottom */}
      {user && (
        <div className="border-t p-4 flex-shrink-0">
          <div className="mb-3">
            <p className="text-sm font-medium text-gray-900 truncate">
              {user.email}
            </p>
            <p className="text-xs text-gray-500 capitalize">
              {user.role === 'partner' && user.partnerType ? `${user.partnerType} partner` : user.role}
            </p>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <LogOut className="h-4 w-4" />
            Logout
          </button>
        </div>
      )}
    </div>
  );

  return (
    <>
      {/* Desktop sidebar - always visible */}
      <aside
        className="hidden md:flex md:flex-col md:fixed md:inset-y-0 md:left-0 bg-white border-r z-30"
        style={{ width: DRAWER_WIDTH }}
      >
        {sidebarContent}
      </aside>

      {/* Mobile drawer - overlay */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/50 z-40 md:hidden"
            onClick={onClose}
            aria-hidden="true"
          />
          {/* Drawer panel */}
          <aside
            className="fixed inset-y-0 left-0 bg-white z-50 md:hidden shadow-xl transform transition-transform"
            style={{ width: DRAWER_WIDTH }}
          >
            {sidebarContent}
          </aside>
        </>
      )}
    </>
  );
}
